import { motion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const glossaryTerms = [
  {
    term: "Authority",
    definition: "Contractually defined decision rights governed by scope, audit, and enforcement.",
  },
  {
    term: "Certification",
    definition: "Formal validation of authority competence; conditional and revocable.",
  },
  {
    term: "License",
    definition: "A revocable grant permitting authority exercise under defined constraints.",
  },
  {
    term: "Enforcement",
    definition: "Systematic mechanisms ensuring compliance, including audits and revocation.",
  },
  {
    term: "Governance",
    definition: "Structural allocation of decision rights independent of individuals.",
  },
  {
    term: "Authority Drift",
    definition: "Deviation from defined authority boundaries over time.",
  },
  {
    term: "Founder Dependency",
    definition: "Operational reliance on individual discretion rather than systems.",
  },
  {
    term: "Audit Trigger",
    definition: "Event or schedule initiating a compliance review.",
  },
  {
    term: "Conditional Status",
    definition: "Temporary restriction pending remediation.",
  },
  {
    term: "Revocation",
    definition: "Permanent withdrawal of authority rights.",
  },
  {
    term: "Transferability",
    definition: "Ability of the system to function post-founder or post-transaction.",
  },
];

const GlossaryPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="glossary-page">
      <SEOHead 
        title="Authority Glossary - OnPoint Authority Systems"
        description="Definitions of key terms within the OnPoint Authority Systems framework: Authority, Certification, License, Enforcement, Governance, and more."
        url="https://onpointauthority.com/glossary"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="glossary-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Reference
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Authority<br />
              <span className="text-primary">Glossary</span>
            </h1>
            <p className="text-lg font-light leading-relaxed text-muted-foreground max-w-2xl">
              Definitions of key terms within the OnPoint Authority Systems framework.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Glossary Terms */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="glossary-terms">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-0">
            {glossaryTerms.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="border-b border-white/5 py-8 first:pt-0"
                data-testid={`glossary-term-${item.term.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h2 className="font-serif text-2xl md:text-3xl font-light text-foreground mb-3">
                  {item.term}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {item.definition}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="glossary-closing">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm text-muted-foreground">
              These definitions are consistent across all OnPoint Authority Systems documentation, 
              including the Confidential Information Memorandum (CIM).
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GlossaryPage;
